import { arrayUnique } from 'common/utilities/core/array';
import { stringIsNumber } from 'common/utilities/core/string';
import { trailingZerosRegExp } from 'common/utilities/regexp/regexp';
import * as I from 'core/api/coreApiClient';
import I18n from 'i18n/en-US';

type ConstraintKey = keyof typeof I18n.utils.constraint;

const stringLiteralToRegExp = (input: string) => {
  const parts = input.match(/^\/(.*?)\/([gimy]*)$/);
  return parts ? new RegExp(parts[1], parts[2]) : new RegExp(input);
};

export const unmatchedConstraints = (constraint: I.Constraint, value?: string | number) => {
  const unmatched: ConstraintKey[] = [];

  if (value === undefined || `${value}`.trim().length === 0) {
    // If a value is mandatory but undefined, the first (and only) message to be shown
    // will be that it's a mandatory field
    if (constraint.mandatory) {
      unmatched.push('mandatory');
      return unmatched;
    }

    // Only evaluate the following constraints if a value has been set.
    // Any missing mandatory value would have been caught above.
    return [];
  }

  if (constraint.javaScriptRegexPattern
    && !value.toString().match(stringLiteralToRegExp(constraint.javaScriptRegexPattern))
  ) {
    unmatched.push('javaScriptRegexPattern');
  }

  if (constraint.length && value.toString().length > constraint.length) {
    unmatched.push('length');
  }

  if (constraint.letterCase) {
    if (constraint.letterCase === 'Upper' && value.toString().toUpperCase() !== value.toString()) {
      unmatched.push('letterCaseUpper');
    } else if (constraint.letterCase === 'Lower' && value.toString().toLowerCase() !== value.toString()) {
      unmatched.push('letterCaseLower');

    // Current regex is a copy of the Java one. Tests are missing because it only covers specific cases.
    // Better regex would be: /(?=^.*[A-Z]+)(?=^[a-zA-Z0-9]*$)/ : minimum of one upercase, and only a-zA-Z0-9 caracteres
    } else if (constraint.letterCase === 'Camel' && !value.toString().match(/[a-z_]([A-Z][a-z_])*/)) {
      unmatched.push('letterCaseCamel');
    }
  }

  if (constraint.precision) {
    const { fractionalDigits, integerDigits } = constraint.precision;

    const valueAsString = value.toString();

    if (!stringIsNumber(valueAsString)) {
      unmatched.push('invalidNumber');
    } else {
      const valueWithoutTrailingZeros = valueAsString.replace(trailingZerosRegExp, '$1$2');

      const processedValue = valueAsString.match(/e/i)
        ? Number(valueWithoutTrailingZeros).toLocaleString(
          'fullwide', { maximumFractionDigits: 20, useGrouping: false },
        )
        : valueWithoutTrailingZeros;

      const [left, right] = processedValue.split('.');
      try {
        // BigInt avoids scientific notation, which doesn't give a proper length
        const absValue = BigInt(Math.abs(Number(left))).toString();
        const digits = absValue.length;

        if (digits > integerDigits) {
          unmatched.push('precision');
        }
      } catch (e) {
        unmatched.push('precision');
      }

      if (fractionalDigits !== undefined && (right || '').length > fractionalDigits) {
        unmatched.push('precision');
      }
    }
  }

  if (constraint.range) {
    if (typeof value === 'string' && !stringIsNumber(value)) {
      unmatched.push('invalidNumber');
    } else {
      let valueAsNumber = Number(value);
      const { asAbsValue, max, min } = constraint.range;
      if (asAbsValue) valueAsNumber = Math.abs(valueAsNumber);

      if (min !== undefined && valueAsNumber < min) {
        unmatched.push('rangeMinMax');
      }
      if (max !== undefined && valueAsNumber > max) {
        unmatched.push('rangeMinMax');
      }
    }
  }

  return arrayUnique(unmatched);
};

/**
 * Format unmatched constraints to readable messages.
 *
 * @param constraint
 * @param value
 */
export const unmatchedConstraintsErrorMsg = (
  constraint: I.Constraint, excludeMsg: ConstraintKey[], value?: string | number,
): I.Message[] => {
  const keys = unmatchedConstraints(constraint, value).filter((key) => !excludeMsg.includes(key));

  return keys.map((key) => ({
    severity: I.SeverityConstants.ERROR,
    text: I18n.utils.constraint[key](constraint),
  }));
};

export const isColorConstraint = (constraint: I.Constraint) => (
  constraint.javaScriptRegexPattern === '/^#[a-fA-F0-9]{6}$/g'
);
